import React from 'react';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { SelectList } from './select-list';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import DoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import ArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import ArrowRight from '@mui/icons-material/KeyboardArrowRight';
import DoneIcon from '@mui/icons-material/Done';
import CancelIcon from '@mui/icons-material/Cancel';
import CardActions from '@mui/material/CardActions';
import { byName, removeFrom } from './lib';
import { DefaultSelectionMode } from './multi-select';

interface TransferListProps<T> {
  selectables: T[];
  selected: T[];
  defaultSelectionMode?: DefaultSelectionMode;
  groupId?: keyof T;
  onDone: (selection: string[]) => void;
  onCancel: () => void;
  itemLabel: (a: T) => string;
  itemValue: (a: T) => string;
  groupLabel?: (groupId: string) => string;
}

export function TransferList<T>(props: TransferListProps<T>) {
  const {
    selectables,
    selected: propsSelected,
    defaultSelectionMode = 'first',
    itemLabel,
    itemValue,
    groupId,
    groupLabel,
    onCancel,
    onDone,
  } = props;
  const [checked, setChecked] = React.useState<T[]>([]);
  const [unchecked, setUnchecked] = React.useState<T[]>([]);

  // Initialize state from parent selection, falling back to configured default mode when empty
  const initialState = React.useMemo(() => {
    const sortedSelectables = [...selectables].sort(byName(itemLabel));
    const selectedIds = new Set(propsSelected.map((entry) => itemValue(entry)));
    const selectedFromProps = sortedSelectables.filter((entry) =>
      selectedIds.has(itemValue(entry)),
    );

    if (selectedFromProps.length > 0) {
      const selectedFromPropsIds = new Set(
        selectedFromProps.map((entry) => itemValue(entry)),
      );
      const remainingItems = sortedSelectables.filter(
        (entry) => !selectedFromPropsIds.has(itemValue(entry)),
      );
      return {
        selectable: remainingItems,
        selected: selectedFromProps,
      };
    }

    if (defaultSelectionMode === 'none') {
      return {
        selectable: sortedSelectables,
        selected: [],
      };
    }

    if (defaultSelectionMode === 'all') {
      return {
        selectable: [],
        selected: sortedSelectables,
      };
    }

    if (sortedSelectables.length > 0) {
      const firstItem = sortedSelectables[0];
      const remainingItems = sortedSelectables.slice(1);
      return {
        selectable: remainingItems,
        selected: [firstItem],
      };
    } else {
      // No items available
      return {
        selectable: sortedSelectables,
        selected: [],
      };
    }
  }, [selectables, propsSelected, defaultSelectionMode, itemLabel, itemValue]);

  const [selectable, setSelectable] = React.useState<T[]>(
    initialState.selectable,
  );
  const [selected, setSelected] = React.useState<T[]>(initialState.selected);

  const handleChecked = (entry: T) => {
    const currentIndex = checked.indexOf(entry);
    const _checked = [...checked];
    if (currentIndex === -1) {
      _checked.push(entry);
    } else {
      _checked.splice(currentIndex, 1);
    }
    setChecked(_checked);
  };

  const handleUnchecked = (entry: T) => {
    const currentIndex = unchecked.indexOf(entry);
    const _unchecked = [...unchecked];
    if (currentIndex === -1) {
      _unchecked.push(entry);
    } else {
      _unchecked.splice(currentIndex, 1);
    }
    setUnchecked(_unchecked);
  };

  const handleSelect = () => {
    setSelected(selected.concat([...checked]).sort(byName(itemLabel)));
    setSelectable(removeFrom(selectable, checked, itemValue));
    setChecked([]);
  };
  const handleSelectAll = () => {
    // Capture current values to avoid stale closure
    const currentSelectable = selectable;
    const currentSelected = selected;

    // Move all items from selectable to selected
    const newSelected = currentSelected
      .concat([...currentSelectable])
      .sort(byName(itemLabel));

    setSelected(newSelected);
    setSelectable([]);
    setChecked([]);
    setUnchecked([]);
  };

  const handleUnselectAll = () => {
    // Capture current values to avoid stale closure
    const currentSelectable = selectable;
    const currentSelected = selected;

    // Move all items from selected to selectable
    const newSelectable = currentSelectable
      .concat([...currentSelected])
      .sort(byName(itemLabel));

    setSelectable(newSelectable);
    setSelected([]);
    setChecked([]);
    setUnchecked([]);
  };

  const handleUnselect = () => {
    setSelectable(selectable.concat([...unchecked]).sort(byName(itemLabel)));
    setSelected(removeFrom(selected, unchecked, itemValue));
    setUnchecked([]);
  };

  const selectedChecked = React.useMemo(
    () => selected.filter((entry) => !unchecked.includes(entry)),
    [selected, unchecked],
  );

  return (
    <Card sx={{ p: 1, width: '100%', padding: '10px' }}>
      <CardContent sx={{ width: '100%', padding: 0 }}>
        <Stack direction={'row'}>
          <SelectList<T>
            header="Available"
            records={selectable}
            checked={checked}
            groupId={groupId}
            handleClick={handleChecked}
            itemLabel={itemLabel}
            itemValue={itemValue}
            groupLabel={groupLabel}
            sx={{
              width: '45%',
              maxWidth: '45%',
              backgroundColor: 'transparent',
            }}
          />
          <Stack
            alignItems={'center'}
            justifyContent={'space-evenly'}
            sx={{
              position: 'relative',
              zIndex: 100,
              width: '10%',
              maxWidth: '10%',
            }}
          >
            <IconButton
              onClick={handleSelect}
              disabled={!checked.length}
              color={checked.length ? 'success' : 'default'}
            >
              <ArrowRight />
            </IconButton>
            <IconButton
              onClick={handleSelectAll}
              disabled={selectable.length === 0}
              color={selectable.length > 0 ? 'success' : 'default'}
            >
              <DoubleArrowRight />
            </IconButton>
            <IconButton
              onClick={handleUnselectAll}
              disabled={selected.length === 0}
              color={selected.length > 0 ? 'success' : 'default'}
            >
              <DoubleArrowLeft />
            </IconButton>
            <IconButton
              onClick={handleUnselect}
              disabled={!unchecked.length}
              color={unchecked.length ? 'success' : 'default'}
            >
              <ArrowLeft />
            </IconButton>
          </Stack>
          <SelectList<T>
            header="Selected"
            records={selected}
            checked={selectedChecked}
            groupId={groupId}
            handleClick={handleUnchecked}
            itemLabel={itemLabel}
            itemValue={itemValue}
            groupLabel={groupLabel}
            sx={{ width: '45%', maxWidth: '45%' }}
          />
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: 'right', gap: 2 }}>
        <Button
          variant={'text'}
          endIcon={<CancelIcon />}
          color={'primary'}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant={'contained'}
          endIcon={<DoneIcon />}
          color={'secondary'}
          sx={{ color: 'white' }}
          onClick={() => {
            onDone(selected.map((s) => itemValue(s)));
            onCancel();
          }}
        >
          Apply
        </Button>
      </CardActions>
    </Card>
  );
}
